import {Dispatch, SetStateAction, useEffect, useMemo, useRef, useState} from "react";

type RowSelection = Record<string, boolean>;

interface UseFilteredRowSelectionOptions<T> {
  data: T[];
  getRowId: (row: T) => string;
  getSubRows?: (row: T) => T[] | undefined;
  clearSelectionOnDataChange?: boolean;
}

interface UseFilteredRowSelectionReturn {
  rowSelection: RowSelection;
  setRowSelection: Dispatch<SetStateAction<RowSelection>>;
}

/**
 * A hook that manages row selection state and automatically filters out
 * selections for rows that are no longer visible (e.g., due to search filtering).
 */
export const useFilteredRowSelection = <T>({
  data,
  getRowId: _getRowId,
  getSubRows: _getSubRows,
  clearSelectionOnDataChange = true,
}: UseFilteredRowSelectionOptions<T>): UseFilteredRowSelectionReturn => {
  const [rowSelection, setRowSelection] = useState<RowSelection>({});

  const getRowId = useRef(_getRowId);
  getRowId.current = useMemo(() => getRowId.current, []);
  const getSubRows = useRef(_getSubRows);
  getSubRows.current = useMemo(() => getSubRows.current, []);

  useEffect(() => {
    if (clearSelectionOnDataChange) {
      setRowSelection((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      return;
    }

    const getAllRowIds = (rows: T[]): Set<string> => {
      const ids = new Set<string>();
      rows.forEach((row) => {
        ids.add(getRowId.current(row));
        const subRows = getSubRows.current?.(row);
        if (subRows) {
          subRows.forEach((subRow) => ids.add(getRowId.current(subRow)));
        }
      });
      return ids;
    };

    const visibleIds = getAllRowIds(data);
    setRowSelection((prev) => {
      const filtered = Object.fromEntries(Object.entries(prev).filter(([id]) => visibleIds.has(id)));
      if (Object.keys(filtered).length !== Object.keys(prev).length) {
        return filtered;
      }
      return prev;
    });
  }, [data, clearSelectionOnDataChange]);

  return {rowSelection, setRowSelection};
};
